/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Beta\FeedbackModal.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Modal de feedback pour les bêta-testeurs
 * Permet de signaler des bugs ou suggérer des améliorations
 */
import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../services/httpClient';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FeedbackType = 'bug' | 'improvement' | 'question';

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [devisList, setDevisList] = useState<any[]>([]);
  const [selectedDevisId, setSelectedDevisId] = useState<string>('');
  const [loadingDevis, setLoadingDevis] = useState(false);

  // Charger la liste des devis quand le modal s'ouvre et que c'est un bug
  useEffect(() => {
    if (isOpen && feedbackType === 'bug') {
      loadDevisList();
    }
  }, [isOpen, feedbackType]);

  const loadDevisList = async () => {
    setLoadingDevis(true);
    try {
      console.log('🔍 Chargement des devis pour le feedback...');
      
      // Recuperer l'ID utilisateur comme DevisApiService
      const userStr = localStorage.getItem('user');
      const userId = userStr ? JSON.parse(userStr).userId : null;
      
      const data = await apiFetch<any>('/devis', {
        method: 'GET',
      });
      console.log('📦 Données reçues:', data);
      
      // L'API peut retourner soit un objet {success, devis} soit directement un tableau
      let devisArray = [];
      if (Array.isArray(data)) {
        // Réponse directe en tableau
        devisArray = data;
        console.log(`✅ ${devisArray.length} devis trouvés (tableau direct)`);
      } else if (data.success && data.devis) {
        // Réponse avec objet wrapper
        devisArray = data.devis;
        console.log(`✅ ${devisArray.length} devis trouvés (objet wrapper)`);
      } else {
        console.warn('⚠️ Format de réponse non reconnu');
        setDevisList([]);
        return;
      }
      
      // IMPORTANT: L'API /api/devis devrait déjà filtrer par utilisateur connecté
      // Si ce n'est pas le cas, c'est un problème de sécurité côté backend à corriger
      // En attendant, on affiche un avertissement si on détecte trop de devis
      if (devisArray.length > 100) {
        console.warn('⚠️ ATTENTION: Nombre suspect de devis (>100). Vérifier le filtrage par utilisateur côté backend!');
      }
      
      // Trier par date (plus récent en premier)
      const sorted = devisArray.sort((a: any, b: any) => 
        new Date(b.date_creation).getTime() - new Date(a.date_creation).getTime()
      );
      setDevisList(sorted);
      console.log('📋 Liste des devis chargée:', sorted.length);
    } catch (error) {
      console.error('❌ Erreur chargement devis:', error);
      setDevisList([]);
    } finally {
      setLoadingDevis(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Récupérer les données complètes du devis si sélectionné
      let devisData = null;
      if (selectedDevisId) {
        try {
          console.log('🔍 Récupération des détails du devis:', selectedDevisId);
          
          // Utiliser les memes headers d'authentification
          const userStr = localStorage.getItem('user');
          const userId = userStr ? JSON.parse(userStr).userId : null;
          
          const devisResult = await apiFetch<any>(`/devis/${selectedDevisId}`, {
            method: 'GET',
          });
          console.log('📦 Données du devis reçues:', devisResult);
          
          if (devisResult.success && devisResult.devis) {
            devisData = devisResult.devis;
            console.log('✅ Devis complet récupéré avec', Object.keys(devisData).length, 'propriétés');
            console.log('📋 Articles:', devisData.articles?.length || 0);
          } else if (devisResult) {
            // Peut-être un tableau ou un objet direct
            devisData = devisResult;
            console.log('✅ Devis récupéré (format alternatif)');
          } else {
            console.warn('⚠️ Aucune donnée de devis reçue');
          }
        } catch (err) {
          console.error('❌ Erreur récupération devis:', err);
        }
      }

      // Envoyer le feedback au backend
      await apiFetch('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          type: feedbackType,
          title,
          description,
          email,
          page: window.location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          devisId: selectedDevisId || null,
          devisData: devisData || null,
        }),
      });

      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        // R?initialiser le formulaire
        setTitle('');
        setDescription('');
        setEmail('');
        setFeedbackType('bug');
        setIsSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('Erreur lors de l\'envoi du feedback:', error);
      alert('Une erreur est survenue. Merci de réessayer ou de nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Centrage du modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Contenu du modal */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">Merci pour votre retour !</h3>
              <p className="mt-2 text-sm text-gray-500">Votre feedback nous aide à améliorer l'application.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" id="modal-title">
                    <span className="mr-2">💬</span>
                    Votre retour d'expérience
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Aidez-nous à améliorer SelleriePro en partageant votre expérience
                  </p>

                  <div className="mt-4 space-y-4">
                    {/* Type de feedback */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type de retour</label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => setFeedbackType('bug')}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            feedbackType === 'bug'
                              ? 'bg-red-100 text-red-800 border-2 border-red-500'
                              : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          🐛 Bug
                        </button>
                        <button
                          type="button"
                          onClick={() => setFeedbackType('improvement')}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            feedbackType === 'improvement'
                              ? 'bg-blue-100 text-blue-800 border-2 border-blue-500'
                              : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          💡 Idée
                        </button>
                        <button
                          type="button"
                          onClick={() => setFeedbackType('question')}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            feedbackType === 'question'
                              ? 'bg-green-100 text-green-800 border-2 border-green-500'
                              : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          ❓ Question
                        </button>
                      </div>
                    </div>

                    {/* Sélection de devis (seulement pour les bugs) */}
                    {feedbackType === 'bug' && (
                      <div>
                        <label htmlFor="devis" className="block text-sm font-medium text-gray-700 mb-1">
                          Devis concerné (optionnel)
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Sélectionnez le devis lié au bug pour nous aider à diagnostiquer plus rapidement
                        </p>
                        {loadingDevis ? (
                          <div className="text-sm text-gray-500 py-2">Chargement des devis...</div>
                        ) : (
                          <select
                            id="devis"
                            value={selectedDevisId}
                            onChange={(e) => setSelectedDevisId(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          >
                            <option value="">-- Aucun devis sélectionné --</option>
                            {devisList.map((devis) => (
                              <option key={devis.id} value={devis.id}>
                                {devis.reference} - {devis.client?.nom || 'Client'} ({devis.montant_ht}€ HT) - {new Date(devis.date_creation).toLocaleDateString('fr-FR')}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    {/* Titre */}
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                        Titre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        required
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Résumé en quelques mots"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="description"
                        required
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Décrivez en détail ce qui s'est passé, ce que vous attendiez, etc."
                      />
                    </div>

                    {/* Email optionnel */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email (optionnel)
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Pour vous contacter si besoin"
                      />
                    </div>

                    {/* Info technique */}
                    <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-600">
                      <p className="font-medium mb-1">ℹ️ Informations automatiques :</p>
                      <p>• Page actuelle : {window.location.pathname}</p>
                      <p>• Navigateur : {navigator.userAgent.split(')')[0].split('(')[1] || 'Inconnu'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Envoi...' : 'Envoyer'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;